// NPM
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { filter, indexOf, map, camelCase, isEmpty, isNil, omitBy, isArray } from "lodash";
import moment from "moment";

// HELPERS
import { encode } from "Helpers/enc";

// STYLE
import "../../../order-management/order-management.scss";

// ANT COMPONENTS
import { ActionMenu, Button, DataTable } from "@life-line-lab/components-react";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  LoadingOutlined,
  MoreOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Col, Row, Switch, Tooltip } from "antd";

// SHARED COMPONENT
import DataTableFilter from "Components/shared/data-table-filter";
import VersionAccordion from "Components/order-management/shared/version-accordion";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
import { getIndexOfOrderSettings } from "Helpers/order-settings-helper";
import { OrderSettingsEnum } from "Data/settings";
import { SettingTabColumns } from "../tab-columns";

interface SettingListComponentProps {
  data?: any;
  isCustomerPage?: any;
  tab: OrderSettingsEnum;
  tabName: string;
  hideModal: any;
  onAddCustomer: any;
  onCreateVersion: any;
  onUpdateVersion: any;
  onActivateCustomerSettings?: any;
  onFilter?: (filterInput) => void;
  onDownloadExcel?: any;
  handleRefresh?: any;
  isLoading?: any;
}

const SettingListComponent: React.FC<SettingListComponentProps> = ({
  tab,
  data,
  isCustomerPage,
  tabName,
  hideModal,
  onAddCustomer,
  onCreateVersion,
  onUpdateVersion,
  onFilter,
  onActivateCustomerSettings,
  onDownloadExcel,
  handleRefresh,
  isLoading = false,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [activeColumns, setActiveColumns] = useState<any>([]);
  const [filterState, setFilterState] = useState<{ [key: string]: string | boolean }>();

  const allPossibleColumns = [
    {
      title: "",
      dataIndex: "",
      key: "switch",
      width: 80,
      render: (_: any, record: any) => {
        return (
          <Switch
            defaultChecked
            disabled={!record.isUpdatable}
            onChange={(value) => {
              onActivateCustomerSettings(value, record.orderSettingID);
            }}
          />
        );
      },
    },
    {
      title: "ID",
      dataIndex: "code",
      key: "code",
      ellipsis: true,
    },
    //For Location and Rate table to view details
    {
      title: t("label.id").toLocaleUpperCase(),
      dataIndex: "code",
      key: "id",
      ellipsis: true,
      render: (_: any, record: any) => <a className="clickable-id">{record.code || ""}</a>,
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleEditSetting(record._id);
          },
        };
      },
    },
    {
      title: "VERSION",
      dataIndex: "version",
      key: "version",
    },
    // {
    //   title: 'HS Code',
    //   dataIndex: 'hsCode',
    //   key: 'hsCode'
    // },
    {
      title: t(`label.${camelCase(tabName)}`).toUpperCase(),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t(`label.customerName`).toUpperCase(),
      dataIndex: "customerName",
      key: "customerName",
      ellipsis: true,
    },
    {
      title: "AREA NAME",
      dataIndex: "areaName",
      key: "areaName",
      ellipsis: true,
    },
    {
      title: "AREA NAME",
      dataIndex: "location",
      key: "location",
      ellipsis: true,
      render: (_: any, record: any) => (
        <React.Fragment>
          {record?.locations?.map((location, index) => (
            <span key={index}>
              {location.areaName}
              <br />
            </span>
          ))}
        </React.Fragment>
      ),
    },
    {
      title: "MAX WGT (KG)",
      dataIndex: "maxWeight",
      key: "maxWeight",
    },
    {
      title: "MAX DIMENSION",
      dataIndex: "maxDimension",
      key: "maxDimension",
      render: (_: any, record: any) => (
        <React.Fragment>
          <span>
            {record?.maxDimension?.length} x {record?.maxDimension?.width} x{" "}
            {record?.maxDimension?.height}
          </span>
        </React.Fragment>
      ),
    },
    {
      title: "STATE",
      dataIndex: "states",
      key: "states",
      render: (_: any, record: any) => (
        <React.Fragment>
          {record?.states?.map((state, i) => (
            <span key={i}>
              {state.state}
              <br />
            </span>
          ))}
        </React.Fragment>
      ),
    },
    {
      title: "POSTCODES",
      dataIndex: ["locations"],
      key: "postcode",
      render: (locations: any, record) => {
        let count: number = 0;

        // this one is crazy, postcodes can be found either in locations or states
        // so do not do any error handling for empty locations and return 0 right away
        // no idea why this is done in the first place

        if (!isEmpty(locations)) {
          // check if locations have states
          locations.forEach((l) => {
            // go through each state and add up the postcodes
            if (!l.states) return;

            l.states.forEach((s) => {
              if (!s) return;
              if (!isArray(s.postcodes)) return;

              count += s.postcodes.length;
            });
          });
        }

        // if the number is 0, perhaps locations do not have states
        if (count === 0) {
          if (!isEmpty(record.states)) {
            // recount using states if any
            record?.states?.forEach((state) => (count += state.postcodes.length));
          }
        }

        return <span>{count}</span>;
      },
    },
    {
      title: "SERVICE TYPES",
      dataIndex: ["locations", "serviceTypes"],
      key: "serviceTypes",
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <React.Fragment>
            {record?.serviceTypes?.map((svt, i) => (
              <span key={i}>
                {svt.name}
                <br />
              </span>
            )) || // the Set is used to remove service type duplicates and [] to return as array
              [
                ...new Set<any>(
                  record?.locations
                    ?.map((e) =>
                      e.deliveryTypes.map((dvt) => dvt.serviceTypes.map((svt) => svt.name)),
                    )
                    .flat(2),
                ),
              ].map((svt, i) => (
                <span key={i}>
                  {svt} <br />
                </span>
              ))}
          </React.Fragment>
        );
      },
    },
    {
      title: "DELIVERY TYPES",
      dataIndex: ["locations", "deliveryTypes"],
      key: "deliveryTypes",
      ellipsis: true,
      render: (_: any, record: any) => (
        <React.Fragment>
          {
            record?.deliveryTypes?.map((dvt, i) => (
              <span key={i}>
                {dvt.name}
                <br />
              </span>
            )) ||
              [
                ...new Set<any>(
                  record?.locations
                    ?.map((location) => location.deliveryTypes.map((dvt) => dvt.name))
                    .flat(1),
                ),
              ].map((dvt, i) => (
                <span key={i}>
                  {dvt}
                  <br />
                </span>
              )) // same Set use
          }
        </React.Fragment>
      ),
    },
    {
      title: "DEFAULT REMARKS",
      dataIndex: "remark",
      key: "remarks",
    },
    {
      title: "REMARKS",
      dataIndex: ["data", "description"],
      key: "remark",
      render: (text: any) => text,
    },
    {
      title: "VALUE FROM (RM)",
      dataIndex: "insuranceCharges",
      key: "valueFrom",
      render: (_: any, record: any) => (
        <React.Fragment>
          {map(record?.insuranceCharges, (row, i) => (
            <span key={i}>
              {row.valueFrom}
              <br />
            </span>
          ))}
        </React.Fragment>
      ),
    },
    {
      title: "VALUE TO (RM)",
      dataIndex: "insuranceCharges",
      key: "valueTo",
      render: (_: any, record: any) => (
        <React.Fragment>
          {map(record?.insuranceCharges, (row, i) => (
            <span key={i}>
              {row.valueTo}
              <br />
            </span>
          ))}
        </React.Fragment>
      ),
    },
    {
      title: "CHARGE",
      dataIndex: "insuranceCharges",
      key: "charge",
      render: (_: any, record: any) => (
        <React.Fragment>
          {map(record?.insuranceCharges, (row, i) => (
            <span key={i}>
              {row.charge}
              <br />
            </span>
          ))}
        </React.Fragment>
      ),
    },
    {
      title: "SURCHARGE",
      dataIndex: "surcharge",
      key: "surcharge",
      render: (_: any, record: any) => {
        return (
          <div className="surcharge-display">
            {record?.surcharge?.fixedAmount
              ? `RM ${record.surcharge.fixedAmount} ${
                  record?.surcharge?.percentage ? `OR ${record.surcharge.percentage}%` : ""
                }`
              : `${record.surcharge?.percentage}%`}
          </div>
        );
      },
    },
    {
      title: "ESTIMATED DELIVERY DAYS",
      dataIndex: "estimatedDeliveryInDay",
      key: "estimatedDeliveryInDay",
      ellipsis: true,
      render: (text: any) => {
        return <span>{text}</span>;
      },
    },
    {
      title: "ESTIMATED TIME",
      dataIndex: "estimatedTime",
      key: "estimatedTime",
      ellipsis: true,
      render: (text: any) => {
        if (!text) return null;

        return moment(text, "HH:mm").format("hh:mm a");
      },
    },
    {
      title: "CUT OFF TIME",
      dataIndex: "cutOffTime",
      key: "cutOffTime",
      ellipsis: true,
      render: (text: any) => {
        if (!text) return null;

        return moment(text, "HH:mm").format("hh:mm a");
      },
    },
    {
      title: "VALID FROM",
      dataIndex: "validFrom",
      key: "validFrom",
      ellipsis: true,
      render: (text: any, record: any) => {
        if (!text) return null;

        return <span>{moment(record?.validFrom).format("DD-MM-YYYY") || null}</span>;
      },
    },
    {
      title: "VALID TO",
      dataIndex: "validTo",
      key: "validTo",
      ellipsis: true,
      render: (text: any, record: any) => {
        if (!text) return null;

        return <span>{moment(record?.validTo).format("DD-MM-YYYY") || null}</span>;
      },
    },
    {
      title: "PROHIBITED?",
      dataIndex: "isProhibited",
      key: "isProhibited",
      render: (_: any, record: any) => {
        return <span>{record.isProhibited == true ? "⚠️Prohibited" : "✅Allowed"}</span>;
      },
    },
    {
      // to enable edit, head to tab-columns.json and add an "active"
      title: "",
      dataIndex: "action",
      key: "action",
      width: 80,
      render: (_: any, record: any) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.edit"),
              onClick: () => handleEditSetting(record._id),
            },
          ]}
        />
      ),
    },
  ];

  const getDataTableColumns = (tab: OrderSettingsEnum) => {
    let currentTabColumns = SettingTabColumns[tab].columns;

    const filterColumns = filter(allPossibleColumns, (colObj) => {
      if (!isCustomerPage && colObj.key == "switch") return;
      return indexOf(currentTabColumns, colObj.key) != -1;
    });

    setActiveColumns(filterColumns);
  };

  useEffect(() => {
    getDataTableColumns(tab);
  }, [tab]);

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text.toLowerCase() }));
  };

  const toTitleCase = (str) => {
    //For format conversion (eg. SPECIAL_CARE -> SpecialCare)
    return str
      .toLowerCase()
      .split("_")
      .map(function (word) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join("");
  };

  const handleCreateSetting = () => {
    history.push(`/order-management/settings/form/${getIndexOfOrderSettings(tab)}`);
  };

  const handleEditSetting = (recordID) => {
    let encodedID = encode(recordID);
    history.push(
      `/order-management/settings/form/${getIndexOfOrderSettings(tab)}/${encodedID}/edit?type=view`,
    );
  };

  return (
    <div className="settings-list">
      <ErrorBoundary>
        <DataTable
          rowKey={(record: any, i) => {
            return i;
          }}
          columns={activeColumns}
          dataSource={data || []}
          scroll={{ x: true }}
          title={() => (
            <Row className="title-row" justify="space-between">
              <Col className="mb-0">
                <DataTableFilter
                  searchPlaceholder={t("label.searchByIdName")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                />
              </Col>
              <Col className="mb-0">
                <div className="op-btn-container">
                  <Tooltip title="Refresh" placement="left">
                    <Button
                      className="op-refresh-btn"
                      icon={isLoading ? <LoadingOutlined /> : <ReloadOutlined />}
                      onClick={() => handleRefresh()}
                      shape="circle"
                    />
                  </Tooltip>
                  {tab !== OrderSettingsEnum.CUSTOMER_RATE_TABLE && (
                    <Button
                      label={`Download` + " " + t(`label.${camelCase(tabName)}`)}
                      type="primary"
                      onClick={() => {
                        onDownloadExcel(data, t(`label.${camelCase(tabName)}`), [], tabName);
                      }}
                    />
                  )}
                  {(tab == OrderSettingsEnum.INSURANCE && data?.length > 0) || isCustomerPage ? (
                    <></>
                  ) : (
                    <Button
                      className="createSetting"
                      label={t(`action.create${toTitleCase(tabName)}`)}
                      type="primary"
                      onClick={handleCreateSetting}
                    />
                  )}
                </div>
              </Col>
            </Row>
          )}
          expandable={{
            rowExpandable: (record) =>
              tab === OrderSettingsEnum.ISSUE_TYPE ||
              (tab === OrderSettingsEnum.TYPE_OF_GOODS && !isCustomerPage)
                ? false
                : true,
            expandIcon: ({ expanded, onExpand, record }) =>
              tab === OrderSettingsEnum.ISSUE_TYPE ||
              record.isUpdatable ||
              (tab === OrderSettingsEnum.TYPE_OF_GOODS && !isCustomerPage) ? null : expanded ? (
                <>
                  <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
                </>
              ) : (
                <>
                  <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
                </>
              ),
            expandedRowRender: (record: any) => {
              return map(record.versions, (version, index) => {
                return (
                  <React.Fragment key={index}>
                    <VersionAccordion
                      isCustomerPage={isCustomerPage}
                      key={version.version}
                      tabKey={tab}
                      versionData={version}
                      hideModal={hideModal}
                      onAddCustomerSubmit={onAddCustomer}
                      onSubmit={onCreateVersion}
                      onUpdate={onUpdateVersion}
                    />
                  </React.Fragment>
                );
              });
            },
          }}
        />
      </ErrorBoundary>
    </div>
  );
};

export default SettingListComponent;
